import React from 'react';
import './PredictionCard.css';

const PredictionCard = ({ 
  prediction, 
  onExpand, 
  expandingPredictions = new Set(), 
  level = 1,
  maxDepth = 4 
}) => {
  // Get type-specific styling
  const getTypeStyles = (type) => {
    switch (type) {
      case 'optimistic':
        return {
          borderColor: '#22c55e',
          backgroundColor: '#f0fdf4',
          typeColor: '#16a34a'
        };
      case 'pessimistic':
        return {
          borderColor: '#ef4444',
          backgroundColor: '#fef2f2',
          typeColor: '#dc2626'
        };
      case 'general':
        return {
          borderColor: '#6b7280',
          backgroundColor: '#f9fafb',
          typeColor: '#4b5563'
        };
      case 'similar':
        return {
          borderColor: '#3b82f6',
          backgroundColor: '#eff6ff',
          typeColor: '#2563eb'
        };
      case 'dissimilar':
        return {
          borderColor: '#8b5cf6',
          backgroundColor: '#f5f3ff',
          typeColor: '#7c3aed'
        };
      default:
        return {
          borderColor: '#6b7280',
          backgroundColor: '#f9fafb',
          typeColor: '#4b5563'
        };
    }
  };

  // Get type display label
  const getTypeLabel = (type) => {
    switch (type) {
      case 'optimistic':
        return '✨ Optimistic';
      case 'pessimistic':
        return '⚠️ Pessimistic';
      case 'general':
        return '○ General';
      case 'similar':
        return '≈ Similar';
      case 'dissimilar':
        return '≠ Dissimilar';
      default:
        return type;
    }
  };

  // Get level-specific label for children
  const getLevelLabel = (level) => {
    switch (level) {
      case 2:
        return 'What happens next:';
      case 3:
        return 'Then what happens:';
      default:
        return 'Next predictions:';
    }
  };

  const styles = getTypeStyles(prediction.type);
  const currentLevel = level || prediction.level || 1;
  const canExpand = currentLevel < maxDepth;
  const hasChildren = prediction.children && prediction.children.length > 0;
  const isExpanding = expandingPredictions.has(prediction.id);

  // Calculate indentation and styling based on level
  const indentationLevel = currentLevel - 1;
  const marginLeft = indentationLevel * 12; // 12px per level
  const cardOpacity = Math.max(0.7, 1 - (indentationLevel * 0.1)); // Slightly fade deeper levels
  const cardScale = Math.max(0.95, 1 - (indentationLevel * 0.02)); // Slightly scale down deeper levels

  const handleExpansionClick = () => {
    if (canExpand && onExpand) {
      onExpand(prediction.id);
    }
  };

  return (
    <div 
      className={`prediction-card level-${currentLevel}`}
      style={{
        borderColor: styles.borderColor,
        backgroundColor: styles.backgroundColor,
        marginLeft: `${marginLeft}px`,
        opacity: cardOpacity,
        transform: `scale(${cardScale})`,
        transformOrigin: 'left top'
      }}
    >
      {/* Connecting line for nested items */}
      {currentLevel > 1 && (
        <div 
          className="connection-line"
          style={{
            borderColor: styles.borderColor
          }}
        />
      )}

      <div className="prediction-header">
        <div className="prediction-meta">
          <span 
            className="prediction-type"
            style={{ color: styles.typeColor }}
          >
            {getTypeLabel(prediction.type)}
          </span>
          <span className="prediction-level">
            Level {currentLevel}
          </span>
        </div>
        {canExpand && (
          <button 
            className={`expand-arrow ${prediction.isExpanded ? 'expanded' : ''} ${isExpanding ? 'loading' : ''}`}
            onClick={handleExpansionClick}
            disabled={isExpanding}
            title={prediction.isExpanded ? 'Collapse' : 'Expand to see what happens next'}
          >
            {isExpanding ? (
              <span className="expand-spinner"></span>
            ) : (
              prediction.isExpanded ? '▼' : '▶'
            )}
          </button>
        )}
      </div>
      
      <div className="prediction-content">
        <p className="prediction-text">
          {prediction.text}
        </p>
      </div>

      {/* Child Predictions */}
      {prediction.isExpanded && hasChildren && (
        <div className="children-container">
          <div className="children-header">
            <span className="children-label">
              {getLevelLabel(currentLevel + 1)}
            </span>
          </div>
          <div className="children-list">
            {prediction.children.map((child) => (
              <PredictionCard 
                key={child.id} 
                prediction={child} 
                onExpand={onExpand}
                expandingPredictions={expandingPredictions}
                level={currentLevel + 1}
                maxDepth={maxDepth}
              />
            ))}
          </div>
        </div>
      )}

      {/* Loading state for child generation */}
      {isExpanding && (
        <div className="children-loading">
          <div className="loading-spinner-small"></div>
          <span>Generating {getLevelLabel(currentLevel + 1).toLowerCase()}</span>
        </div>
      )}
    </div>
  );
};

export default PredictionCard; 